import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { startUnreadPolling, useUnreadMessages } from '@/core/inboxBadge';
import { t } from '../i18n/t';

/**
 * Round 129b: a learner who has not finished setting up (profile, placement, trial) cannot open any lesson page - and could not open their inbox either,
 * so a support reply to their report was invisible to them. This small "Messages" link (with the unread count) appears on the onboarding pages ONLY and
 * leads to /messages, the single page that is open at every step. It opens nothing else. Once setup is complete the normal navigation carries the same link.
 */
export default function OnboardingMessagesLink() {
  const { user } = useAuthContext();
  const { pathname } = useLocation();
  const visible = !!user && pathname.startsWith('/onboarding/');
  const unread = useUnreadMessages();
  useEffect(() => (visible ? startUnreadPolling() : undefined), [visible]);
  if (!visible) return null;
  return (
    <Link to="/messages" data-testid="onboarding-messages-link"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-1.5 rounded-full border border-line bg-card px-3 py-1.5 text-xs font-medium text-ink shadow-sm"
      aria-label={unread > 0 ? `${t('common:navigation.messages')} (${t('messages:messagesPage.unreadCount', { count: unread })})` : t('common:navigation.messages')}>
      <span>{t('common:navigation.messages')}</span>
      {unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="onboarding-unread-badge">{unread > 9 ? '9+' : unread}</span>}
    </Link>
  );
}
